import { loadGsap } from './gsap-loader.js';

/**
 * Journal and article pages. Cards rise into place as they scroll into view, and the article's share button
 * copies the link. Everything is visible without GSAP or with reduced motion.
 */
class JournalComponent extends HTMLElement {
  #abort = new AbortController();
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    const { signal } = this.#abort;
    this.querySelector('[data-share]')?.addEventListener('click', (event) => this.#share(event.currentTarget), { signal });

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    loadGsap().then((gsap) => {
      if (!gsap || !this.isConnected) return;
      this.#reveal(gsap);
    });
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#observer?.disconnect();
  }

  /** @param {any} gsap */
  #reveal(gsap) {
    const items = [...this.querySelectorAll('[data-reveal]')];
    if (!items.length) return;
    gsap.set(items, { opacity: 0, y: 32 });

    this.#observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting).map((entry) => entry.target);
        if (!visible.length) return;
        visible.forEach((item) => this.#observer?.unobserve(item));
        gsap.to(visible, {
          opacity: 1,
          y: 0,
          duration: 0.9,
          ease: 'expo.out',
          stagger: 0.08,
          clearProps: 'opacity,transform',
        });
      },
      { rootMargin: '0px 0px -8% 0px' }
    );
    items.forEach((item) => this.#observer?.observe(item));
  }

  /** @param {Element} button */
  async #share(button) {
    const url = button.getAttribute('data-url') || location.href;
    const label = button.textContent;
    try {
      if (navigator.share && matchMedia('(pointer: coarse)').matches) {
        await navigator.share({ url, title: document.title });
        return;
      }
      await navigator.clipboard.writeText(url);
      button.textContent = 'Link copied ✓';
      setTimeout(() => (button.textContent = label), 1800);
    } catch {
      // Sharing was cancelled or isn't available; there's nothing to undo.
    }
  }
}

if (!customElements.get('journal-component')) {
  customElements.define('journal-component', JournalComponent);
}
